import React, {useEffect, useState} from 'react';
import {RequestVisitorDto} from "../../../../../../modules/vms/dto/RequestVisitorDto";
import {useIntl} from "react-intl";
import {ValidateUtils} from "../../../../../../utils/ValidateUtils";
import {
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    Grid,
    IconButton, List, ListItem, ListItemText, Stack
} from "@mui/material";
import CancelButton from "../../../../../../components/buttons/CancelButton";
import SaveButton from "../../../../../../components/buttons/SaveButton";
import FormFieldText from "../../../../../../components/FormFieldText";
import FormFieldSelect from "../../../../../../components/FormFieldSelect";
import {VisitorDriveType} from "../../../../../../modules/vms/enums/VisitorDriveType";
import KeyboardIcon from "@mui/icons-material/Keyboard";
import PlateNumberDialog from "../../../../../../components/PlateNumberDialog";
import dayjs from "dayjs";
import {Formats} from "../../../../../../constants/Formats";
import {FileApi} from "../../../../../../modules/ftp/api/FileApi";
import {HttpStatus} from "../../../../../../modules/common/enums/HttpStatus";
import {RequestVisitorAttachFile} from "../../../../../../modules/vms/dto/RequestVisitorAttachFile";
import {VisitorAttachFileClassify} from "../../../../../../modules/vms/enums/VisitorFileClassify";
import UploadFileButton from "../../../../../../components/buttons/UploadFileButton";
import DeleteIcon from "@mui/icons-material/Delete";
import {PlateNumberColor} from "../../../../../../modules/vms/enums/PlateNumberColor";
import {VehicleType} from "../../../../../../modules/vms/enums/VehicleType";
import FormFieldDate from "../../../../../../components/FormFieldDate";
import {VehicleFuelType} from "../../../../../../modules/vms/enums/VehicleFuelType";
import {VehicleEmissionStandard} from "../../../../../../modules/vms/enums/VehicleEmissionStandard";

export type VisitorDetailDialogProps = {
    onCancel: () => void;
    onOk: (requestVisitor: RequestVisitorDto) => void;
    open: boolean;
    requestVisitor: RequestVisitorDto;
    visitorIndex: number;
};

const VisitorDetailDialog: React.FC<VisitorDetailDialogProps> = (props) => {
    const intl = useIntl();

    const [messageMap, setMessageMap] = useState(new Map<string, string>());
    const [innerVisitor, setInnerVisitor] = useState<RequestVisitorDto>(props.requestVisitor);
    const [openPlateNumberDialog, setOpenPlateNumberDialog] = useState(false);

    useEffect(() => {
        setInnerVisitor(props.requestVisitor);
    }, [props.requestVisitor]);

    const handleChange = (name: string, value: any) => {
        setInnerVisitor({...innerVisitor, [name]: value});
    };

    const handleOk = () => {
        if (!validate()) {
            return;
        }
        props.onOk(innerVisitor);
    };

    const handleClose = () => {
        props.onCancel();
    };

    const validate = () => {
        const newMessageMap = new Map<string, string>();
        if (innerVisitor.lastName === '') {
            newMessageMap.set('lastName', intl.formatMessage({id: 'VMS_REQUEST_VISITOR_LAST_NAME_REQUIRED'}));
        }
        if (innerVisitor.firstName === '') {
            newMessageMap.set('firstName', intl.formatMessage({id: 'VMS_REQUEST_VISITOR_FIRST_NAME_REQUIRED'}));
        }
        if (innerVisitor.mobile === '') {
            newMessageMap.set('mobile', intl.formatMessage({id: 'VMS_REQUEST_VISITOR_MOBILE_REQUIRED'}));
        } else if (!ValidateUtils.isMobileNumber(innerVisitor.mobile)) {
            newMessageMap.set('mobile', intl.formatMessage({id: 'VMS_REQUEST_VISITOR_MOBILE_FORMAT_ERROR'}));
        }
        // if (innerVisitor.email && !ValidateUtils.isEmail(innerVisitor.email)) {
        //     newMessageMap.set('email', intl.formatMessage({id: 'COMMON_EMAIL_FORMAT'}));
        // }
        if (innerVisitor.company === '') {
            newMessageMap.set('company', intl.formatMessage({id: 'VMS_REQUEST_VISITOR_COMPANY_REQUIRED'}));
        }
        if (innerVisitor.plateNumber === '') {
            newMessageMap.set('plateNumber', intl.formatMessage({id: 'VMS_REQUEST_VISITOR_PLATE_NUMBER_REQUIRED'}));
        }

        setMessageMap(newMessageMap);
        return newMessageMap.size <= 0;
    };

    const handleUpload = async (file: File, classify: string) => {
        let targetFolder = dayjs().format(Formats.DATE_SIMPLE);
        targetFolder = targetFolder.substring(0, 6) + "/" + targetFolder.substring(6, 7);
        const responseResult = await FileApi.uploadFile(targetFolder, "", file);
        if (responseResult.status === HttpStatus.SUCCESS) {
            // 更新访客对象的附件列表
            const newAttachFiles = [...innerVisitor.attachFilesArray];
            const newAttachFile = new RequestVisitorAttachFile();
            newAttachFile.fileId = responseResult.data;
            newAttachFile.classify = classify;
            newAttachFile.fileName = file.name;
            newAttachFiles.push(newAttachFile);
            handleChange("attachFilesArray", newAttachFiles);
        }
    }

    const handleRemoveAttachFile = (index: number) => {
        const newAttachFiles = [...innerVisitor.attachFilesArray];
        newAttachFiles.splice(index, 1);
        handleChange('attachFilesArray', newAttachFiles);
    }

    return (
        <Dialog open={props.open} onClose={handleClose} fullWidth={true}>
            <PlateNumberDialog open={openPlateNumberDialog} onOk={(value) => handleChange("plateNumber", value)}
                               onClose={() => setOpenPlateNumberDialog(false)}
                               value={innerVisitor.plateNumber}/>
            <DialogTitle>
                {props.visitorIndex >= 0 ? intl.formatMessage({id: 'VMS_REQUEST_VISITOR_MODIFY'}) : intl.formatMessage({id: 'VMS_REQUEST_VISITOR_CREATE'})}
            </DialogTitle>
            <DialogContent>
                <Grid container columnSpacing={2}>
                    <Grid item xs={6}>
                        <FormFieldText label={intl.formatMessage({id: "VMS_REQUEST_VISITOR_LAST_NAME"})}
                                       placeholder={intl.formatMessage({id: "VMS_REQUEST_VISITOR_LAST_NAME_REQUIRED"})}
                                       required={true} errorText={messageMap.get("lastName")}
                                       value={innerVisitor.lastName}
                                       onChange={(value) => {
                                           handleChange("lastName", value)
                                       }}/>
                    </Grid>
                    <Grid item xs={6}>
                        <FormFieldText label={intl.formatMessage({id: "VMS_REQUEST_VISITOR_FIRST_NAME"})}
                                       placeholder={intl.formatMessage({id: "VMS_REQUEST_VISITOR_FIRST_NAME_REQUIRED"})}
                                       required={true} errorText={messageMap.get("firstName")}
                                       value={innerVisitor.firstName}
                                       onChange={(value) => {
                                           handleChange("firstName", value)
                                       }}/>
                    </Grid>
                    <Grid item xs={6}>
                        <FormFieldText label={intl.formatMessage({id: "VMS_REQUEST_VISITOR_MOBILE"})}
                                       placeholder={intl.formatMessage({id: "VMS_REQUEST_VISITOR_MOBILE_REQUIRED"})}
                                       required={true} errorText={messageMap.get("mobile")}
                                       type={"number"}
                                       value={innerVisitor.mobile}
                                       onChange={(value) => {
                                           handleChange("mobile", value)
                                       }}/>
                    </Grid>
                    {/*<Grid item xs={6}>*/}
                    {/*    <FormFieldText label={intl.formatMessage({id: "VMS_REQUEST_VISITOR_EMAIL"})}*/}
                    {/*                   placeholder={intl.formatMessage({id: "VMS_REQUEST_VISITOR_EMAIL_REQUIRED"})}*/}
                    {/*                   errorText={messageMap.get("email")}*/}
                    {/*                   type={"email"}*/}
                    {/*                   value={innerVisitor.email}*/}
                    {/*                   onChange={(value) => {*/}
                    {/*                       handleChange("email", value)*/}
                    {/*                   }}/>*/}
                    {/*</Grid>*/}
                    <Grid item xs={6}>
                        <FormFieldText label={intl.formatMessage({id: "VMS_REQUEST_VISITOR_COMPANY"})}
                                       placeholder={intl.formatMessage({id: "VMS_REQUEST_VISITOR_COMPANY_REQUIRED"})}
                                       required={true} errorText={messageMap.get("company")}
                                       value={innerVisitor.company}
                                       onChange={(value) => {
                                           handleChange("company", value)
                                       }}/>
                    </Grid>
                    <Grid item xs={6}>
                        <FormFieldSelect label={intl.formatMessage({id: "VMS_REQUEST_VISITOR_DRIVE_TYPE"})}
                                         value={innerVisitor.driveType}
                                         onChange={(value) => {
                                             handleChange("driveType", value)
                                         }}
                                         items={VisitorDriveType.List.map((item) => {
                                             return {label: intl.formatMessage({id: item.label}), value: item.value}
                                         })}/>
                    </Grid>
                    <Grid item xs={6}>
                        <FormFieldText label={intl.formatMessage({id: "VMS_REQUEST_VISITOR_PLATE_NUMBER"})}
                                       placeholder={intl.formatMessage({id: "VMS_REQUEST_VISITOR_PLATE_NUMBER_REQUIRED"})}
                                       value={innerVisitor.plateNumber}
                                       required={true} errorText={messageMap.get("plateNumber")}
                                       endAdornment={
                                           <IconButton color="primary" onClick={() => setOpenPlateNumberDialog(true)}>
                                               <KeyboardIcon/>
                                           </IconButton>}/>
                    </Grid>
                    <Grid item xs={6}>
                        <FormFieldSelect label={intl.formatMessage({id: "VMS_REQUEST_VISITOR_PLATE_NUMBER_COLOR"})}
                                         value={innerVisitor.plateNumberColor}
                                         onChange={(value) => {
                                             handleChange("plateNumberColor", value)
                                         }}
                                         items={PlateNumberColor.List.map((item) => {
                                             return {label: intl.formatMessage({id: item.label}), value: item.value}
                                         })}/>
                    </Grid>
                    <Grid item xs={6}>
                        <FormFieldSelect label={intl.formatMessage({id: "VMS_REQUEST_VISITOR_VEHICLE_TYPE"})}
                                         value={innerVisitor.vehicleType}
                                         onChange={(value) => {
                                             handleChange("vehicleType", value)
                                         }}
                                         items={VehicleType.List.map((item) => {
                                             return {label: intl.formatMessage({id: item.label}), value: item.value}
                                         })}/>
                    </Grid>
                    <Grid item xs={6}>
                        <FormFieldDate label={intl.formatMessage({id: "VMS_REQUEST_VISITOR_VEHICLE_REGISTRATION_DATE"})}
                                       value={dayjs(innerVisitor.vehicleRegistrationDate, Formats.DATE_SIMPLE)}
                                       onChange={(value) => handleChange("vehicleRegistrationDate", value)}/>

                    </Grid>
                    <Grid item xs={6}>
                        <FormFieldSelect label={intl.formatMessage({id: "VMS_REQUEST_VISITOR_VEHICLE_FUEL_TYPE"})}
                                         value={innerVisitor.vehicleFuelType}
                                         onChange={(value) => {
                                             handleChange("vehicleFuelType", value)
                                         }}
                                         items={VehicleFuelType.List.map((item) => {
                                             return {label: intl.formatMessage({id: item.label}), value: item.value}
                                         })}/>
                    </Grid>
                    <Grid item xs={6}>
                        <FormFieldSelect label={intl.formatMessage({id: "VMS_REQUEST_VISITOR_VEHICLE_EMISSION_STANDARD"})}
                                         value={innerVisitor.vehicleEmissionStandard}
                                         onChange={(value) => {
                                             handleChange("vehicleEmissionStandard", value)
                                         }}
                                         items={VehicleEmissionStandard.List.map((item) => {
                                             return {label: intl.formatMessage({id: item.label}), value: item.value}
                                         })}/>
                    </Grid>
                    <Grid item xs={6}>
                        <FormFieldText label={intl.formatMessage({id: "VMS_REQUEST_VISITOR_ID_NUMBER"})}
                                       placeholder={intl.formatMessage({id: "VMS_REQUEST_VISITOR_ID_NUMBER_REQUIRED"})}
                                       value={innerVisitor.idNumber}
                                       onChange={(value) => {
                                           handleChange("idNumber", value)
                                       }}/>
                    </Grid>
                    <Grid item xs={6}>
                        <FormControl variant={'standard'} margin={'normal'}>
                            <Stack spacing={2}>
                                <UploadFileButton
                                    text={intl.formatMessage({id: 'VMS_REQUEST_VISITOR_ATTACH_FILE_CLASSIFY_QUALIFICATION_CERTIFICATE'})}
                                    onChange={(files) => {
                                        if (files) {
                                            for (let file of files) {
                                                handleUpload(file, VisitorAttachFileClassify.Enum.QualificationCertificate);
                                            }
                                        }
                                    }}/>
                                <List>
                                    {innerVisitor.attachFilesArray.map((item, index) => {
                                        if (item.classify === VisitorAttachFileClassify.Enum.QualificationCertificate) {
                                            return <ListItem key={index}
                                                             secondaryAction={
                                                                 <IconButton edge="end" aria-label="delete"
                                                                             onClick={() => handleRemoveAttachFile(index)}>
                                                                     <DeleteIcon/>
                                                                 </IconButton>
                                                             }>
                                                <ListItemText primary={item.fileName}/>
                                            </ListItem>
                                        }
                                    })}
                                </List>
                            </Stack>
                        </FormControl>
                    </Grid>
                    <Grid item xs={6}>
                        <FormControl variant={'standard'} margin={'normal'}>
                            <Stack spacing={2}>
                                <UploadFileButton
                                    text={intl.formatMessage({id: 'VMS_REQUEST_VISITOR_ATTACH_FILE_CLASSIFY_VEHICLE_LICENSE'})}
                                    onChange={(files) => {
                                        if (files) {
                                            for (let file of files) {
                                                handleUpload(file, VisitorAttachFileClassify.Enum.VehicleLicense);
                                            }
                                        }
                                    }}/>
                                <List>
                                    {innerVisitor.attachFilesArray.map((item, index) => {
                                        if (item.classify === VisitorAttachFileClassify.Enum.VehicleLicense) {
                                            return <ListItem key={index}
                                                             secondaryAction={
                                                                 <IconButton edge="end" aria-label="delete"
                                                                             onClick={() => handleRemoveAttachFile(index)}>
                                                                     <DeleteIcon/>
                                                                 </IconButton>
                                                             }>
                                                <ListItemText primary={item.fileName}/>
                                            </ListItem>
                                        }
                                    })}
                                </List>
                            </Stack>
                        </FormControl>
                    </Grid>
                    <Grid item xs={6}>
                        <FormControl variant={'standard'} margin={'normal'}>
                            <Stack spacing={2}>
                                <UploadFileButton
                                    text={intl.formatMessage({id: 'VMS_REQUEST_VISITOR_ATTACH_FILE_CLASSIFY_ROAD_OPERATION_LICENSE'})}
                                    onChange={(files) => {
                                        if (files) {
                                            for (let file of files) {
                                                handleUpload(file, VisitorAttachFileClassify.Enum.RoadOperationLicense);
                                            }
                                        }
                                    }}/>
                                <List>
                                    {innerVisitor.attachFilesArray.map((item, index) => {
                                        if (item.classify === VisitorAttachFileClassify.Enum.RoadOperationLicense) {
                                            return <ListItem key={index}
                                                             secondaryAction={
                                                                 <IconButton edge="end" aria-label="delete"
                                                                             onClick={() => handleRemoveAttachFile(index)}>
                                                                     <DeleteIcon/>
                                                                 </IconButton>
                                                             }>
                                                <ListItemText primary={item.fileName}/>
                                            </ListItem>
                                        }
                                    })}
                                </List>
                            </Stack>
                        </FormControl>
                    </Grid>
                    <Grid item xs={6}>
                        <FormControl variant={'standard'} margin={'normal'}>
                            <Stack spacing={2}>
                                <UploadFileButton
                                    text={intl.formatMessage({id: 'VMS_REQUEST_VISITOR_ATTACH_FILE_CLASSIFY_VEHICLE_SIDE_PHOTO'})}
                                    onChange={(files) => {
                                        if (files) {
                                            for (let file of files) {
                                                handleUpload(file, VisitorAttachFileClassify.Enum.VehicleSidePhoto);
                                            }
                                        }
                                    }}/>
                                <List>
                                    {innerVisitor.attachFilesArray.map((item, index) => {
                                        if (item.classify === VisitorAttachFileClassify.Enum.VehicleSidePhoto) {
                                            return <ListItem key={index}
                                                             secondaryAction={
                                                                 <IconButton edge="end" aria-label="delete"
                                                                             onClick={() => handleRemoveAttachFile(index)}>
                                                                     <DeleteIcon/>
                                                                 </IconButton>
                                                             }>
                                                <ListItemText primary={item.fileName}/>
                                            </ListItem>
                                        }
                                    })}
                                </List>
                            </Stack>
                        </FormControl>
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions>
                <CancelButton onClick={handleClose}/>
                <SaveButton onClick={handleOk}/>
            </DialogActions>
        </Dialog>
    );
};

export default VisitorDetailDialog;
